import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
	type Dispatch,
	type ReactNode,
	type SetStateAction,
} from 'react';
import useScreenSize from './screenSize';

export interface LeftHandLayoutBreadcrumbs {
	label: string;
	href: string;
}

export const paths = {
	home: '/',
	projects: '/projects',
	servers: '/servers',
	serverImages: '/servers/images',
	serverScheduledTasks: '/servers/scheduled-tasks',
	loadBalancers: '/services/load-balancers',
	advancedServices: '/services/advanced-services',
	backupsAndRecovery: '/storage/backups-and-recovery',
	cloudBlockStorage: '/services/cloud-block-storage',
	objectStorage: '/services/object-storage',
	vpns: '/services/vpns',
	CloudIPAddresses: '/network/cloud-ip-addresses',
	cloudPrivateNetwork: '/services/cloud-private-network',
	firewallRulesets: '/services/firewall-rulesets',
	firewalls: '/services/firewalls',
	serverProtection: '/security/server-protection',
	domains: '/domain',
	domainReverseDns: '/domain/dns/reverse-dns',
	domainSsl: '/domain/ssl',
	cloudflare: '/domain/cloudflare',
	globalPrivateFabric: '/network/global-private-fabric',
	premiumBusinessEmail: '/services/premium-business-email',
} as const;

export type Path = (typeof paths)[keyof typeof paths];

/**
 * A configuration of nav items in their respective sections.
 * This configuration object is necessary because we need to keep the nav items in sync with the `open` state of the section each nav item is in. For example, when a user navigates to `/servers/images`, the `servers` section should be open.
 */
export const navSections = {
	servers: [
		paths.servers,
		paths.serverImages,
		paths.serverScheduledTasks,
	] as const,
	hosting: [paths.advancedServices] as const,
	storage: [
		paths.backupsAndRecovery,
		paths.cloudBlockStorage,
		paths.objectStorage,
	] as const,
	network: [
		paths.vpns,
		paths.CloudIPAddresses,
		paths.cloudPrivateNetwork,
		paths.globalPrivateFabric,
		paths.loadBalancers,
	] as const,
	security: [
		paths.cloudflare,
		paths.firewallRulesets,
		paths.firewalls,
		paths.serverProtection,
	] as const,
	domains: [paths.domains, paths.domainReverseDns, paths.domainSsl] as const,
};

const isServersSectionPath = (
	path: string,
): path is (typeof navSections)['servers'][number] => {
	return navSections.servers.includes(
		path as (typeof navSections)['servers'][number],
	);
};

const isHostingSectionPath = (
	path: string,
): path is (typeof navSections)['hosting'][number] => {
	return navSections.hosting.includes(
		path as (typeof navSections)['hosting'][number],
	);
};

const isStorageSectionPath = (
	path: string,
): path is (typeof navSections)['storage'][number] => {
	return navSections.storage.includes(
		path as (typeof navSections)['storage'][number],
	);
};

const isNetworkSectionPath = (
	path: string,
): path is (typeof navSections)['network'][number] => {
	return navSections.network.includes(
		path as (typeof navSections)['network'][number],
	);
};

const isSecuritySectionPath = (
	path: string,
): path is (typeof navSections)['security'][number] => {
	return navSections.security.includes(
		path as (typeof navSections)['security'][number],
	);
};

const isDomainsSectionPath = (
	path: string,
): path is (typeof navSections)['domains'][number] => {
	return navSections.domains.includes(
		path as (typeof navSections)['domains'][number],
	);
};

interface LeftHandLayoutContext {
	title: string;
	setTitle: Dispatch<SetStateAction<string>>;
	breadcrumbs?: LeftHandLayoutBreadcrumbs[];
	setBreadcrumbs: Dispatch<
		SetStateAction<LeftHandLayoutBreadcrumbs[] | undefined>
	>;
	appPath?: string;
	path?: string;
	setPath: Dispatch<SetStateAction<Path | undefined>>;
	screenSize: ReturnType<typeof useScreenSize>;
	mobileOpen: boolean;
	setMobileOpen: Dispatch<SetStateAction<boolean>>;
	leftSize: number;
	setLeftSize: Dispatch<SetStateAction<number>>;
	topSize: number;
	setTopSize: Dispatch<SetStateAction<number>>;
	topInnerX: number;
	setTopInnerX: Dispatch<SetStateAction<number>>;
	topOuterY: number;
	setTopOuterY: Dispatch<SetStateAction<number>>;
	linkComponent?: ReactNode;
	linkProp?: string;
	roles: {
		loggedIn?: boolean;
		keroOnly?: boolean;
		basketAdmin?: boolean;
		accountLogin?: boolean;
		suspended?: boolean;
		terminated?: boolean;
		verificationRequired?: boolean;
	};
	navSectionsOpen: {
		servers: boolean;
		hosting: boolean;
		storage: boolean;
		network: boolean;
		security: boolean;
		domains: boolean;
	};
	toggleNavSection: (
		label: keyof LeftHandLayoutContext['navSectionsOpen'],
	) => void;
}

const rolesDefault: LeftHandLayoutContext['roles'] = {};

const context = createContext<LeftHandLayoutContext | null>(null);

export function useLeftHandLayout() {
	const leftHandLayout = useContext(context);
	if (!leftHandLayout) {
		throw new Error(
			'useLeftHandLayout must be used within a LeftHandLayoutProvider',
		);
	}

	return leftHandLayout;
}

export interface LeftHandLayoutProviderProps {
	appPath?: LeftHandLayoutContext['appPath'];
	linkComponent?: LeftHandLayoutContext['linkComponent'];
	linkProp?: LeftHandLayoutContext['linkProp'];
	roles?: LeftHandLayoutContext['roles'];
	children: ReactNode;
}

export function LeftHandLayoutProvider(props: LeftHandLayoutProviderProps) {
	const {
		appPath,
		linkComponent,
		linkProp,
		roles = rolesDefault,
		children,
	} = props;

	const [title, setTitle] = useState('');
	const [breadcrumbs, setBreadcrumbs] = useState<
		LeftHandLayoutBreadcrumbs[] | undefined
	>(undefined);
	const [pathOverride, setPath] = useState<Path | undefined>(undefined);
	const [leftSize, setLeftSize] = useState(0);
	const [topSize, setTopSize] = useState(0);
	const [topInnerX, setTopInnerX] = useState(0);
	const [topOuterY, setTopOuterY] = useState(0);
	const [mobileOpen, setMobileOpen] = useState(false);
	const [navSectionsOpen, setNavSectionsOpen] = useState({
		servers: true,
		hosting: false,
		storage: false,
		network: false,
		security: false,
		domains: false,
	});

	const toggleNavSection = useCallback(
		(label: keyof typeof navSectionsOpen) => {
			setNavSectionsOpen((prev) => ({
				...prev,
				[label]: !prev[label],
			}));
		},
		[],
	);

	const path = pathOverride || appPath;

	const screenSize = useScreenSize();

	// Open the nav section that the current path is in
	useEffect(() => {
		if (path === undefined) return;

		if (isServersSectionPath(path)) {
			setNavSectionsOpen((prev) => ({
				...prev,
				servers: true,
			}));

			return;
		}

		if (isHostingSectionPath(path)) {
			setNavSectionsOpen((prev) => ({
				...prev,
				hosting: true,
			}));

			return;
		}

		if (isStorageSectionPath(path)) {
			setNavSectionsOpen((prev) => ({
				...prev,
				storage: true,
			}));
		}

		if (isNetworkSectionPath(path)) {
			setNavSectionsOpen((prev) => ({
				...prev,
				network: true,
			}));
		}

		if (isSecuritySectionPath(path)) {
			setNavSectionsOpen((prev) => ({
				...prev,
				security: true,
			}));
		}

		if (isDomainsSectionPath(path)) {
			setNavSectionsOpen((prev) => ({
				...prev,
				domains: true,
			}));
		}
	}, [path]);

	// Close mobile drawer when appPath changes
	useEffect(() => {
		if (appPath === undefined) return;
		setMobileOpen(false);
	}, [appPath]);

	const value: LeftHandLayoutContext = useMemo(
		() => ({
			title,
			setTitle,
			breadcrumbs,
			setBreadcrumbs,
			appPath,
			roles,
			path,
			setPath,
			screenSize,
			mobileOpen,
			setMobileOpen,
			linkComponent,
			linkProp,
			leftSize,
			setLeftSize,
			topSize,
			setTopSize,
			topInnerX,
			setTopInnerX,
			topOuterY,
			setTopOuterY,
			navSectionsOpen,
			toggleNavSection,
		}),
		[
			title,
			setTitle,
			breadcrumbs,
			setBreadcrumbs,
			appPath,
			roles,
			path,
			setPath,
			screenSize,
			mobileOpen,
			setMobileOpen,
			linkComponent,
			linkProp,
			leftSize,
			setLeftSize,
			topSize,
			setTopSize,
			topInnerX,
			setTopInnerX,
			topOuterY,
			setTopOuterY,
			navSectionsOpen,
			toggleNavSection,
		],
	);

	return <context.Provider value={value}>{children}</context.Provider>;
}

export default useLeftHandLayout;
